import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { TRAINING_VIEW_ALL, audienceLabel, filterByWords, formatMinutes, programSummary, trainingError } from "../lib/training";
import { hasPermission } from "../lib/permissions";
import { useAuth } from "../contexts/AuthContext";
import useJamoraCatalog from "../hooks/useJamoraCatalog";
import { AudienceFields } from "../components/training/ProgramBits";
import SessionsPanel from "../components/training/SessionsPanel";
import { StatePill } from "../components/CollaboratorBits";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, BookOpen, ChevronRight, Download, GraduationCap, Loader2, Plus, RefreshCw, Search } from "lucide-react";

const TABS = [["sessions", "Sessions"], ["programmes", "Programmes"], ["modules", "Modules"]];

// Formations (Ordre 11): sessions (who was trained, with what result), programmes (versioned paths made of modules) and modules.
// Reading: any training key or a missions key (route); what each person may do is told by the server (`can_manage`,
// `can_create_without_mission`).
export default function Trainings() {
  const [params, setParams] = useSearchParams();
  const tab = ["programmes", "modules"].includes(params.get("tab")) ? params.get("tab") : "sessions";
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState({ phase: "loading", programs: [], modules: [], canManage: false, error: "" });
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [sessionMeta, setSessionMeta] = useState({ canCreate: false });
  const [exportOpen, setExportOpen] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  const catalog = useJamoraCatalog(true);

  const load = useCallback(async () => {
    try {
      const [programs, modules] = await Promise.all([api.get("/training/programs"), api.get("/training/modules")]);
      setState({ phase: "ready", programs: programs.data.items, modules: modules.data.items, canManage: programs.data.can_manage, error: "" });
    } catch (e) {
      setState((s) => ({ ...s, phase: "error", error: trainingError(e, "Impossible de charger les formations.") }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Router state, consumed once: « Nouveau programme » / « Exporter » from the global search, « mes sessions » from the dashboard.
  useEffect(() => {
    const st = location.state;
    if (!st) return;
    if (st.newProgram) setCreating(true);
    if (st.openExport) setExportOpen(true);
    if (st.sessions === "mine") setMineOnly(true);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location, navigate]);

  const switchTab = (key) => { setSearch(""); setMineOnly(false); setParams(key === "sessions" ? {} : { tab: key }, { replace: true }); };
  const programs = filterByWords(state.programs, search, (p) => `${p.label} ${p.description || ""}`);
  const modules = filterByWords(state.modules, search, (m) => `${m.title} ${m.objectives.map((o) => o.text).join(" ")} ${m.features.map((f) => f.label).join(" ")}`);

  return (
    <div className="space-y-4" data-testid="trainings-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Formations</h1>
          <p className="text-sm text-slate-500">Les programmes que l'équipe Jamora anime, construits à partir de modules réutilisables.</p>
        </div>
        {tab === "sessions" && (
          <div className="flex gap-2 shrink-0">
            {hasPermission(user, TRAINING_VIEW_ALL) && (
              <Button variant="outline" className="h-9" onClick={() => setExportOpen(true)} data-testid="sessions-export"><Download size={16} className="mr-1.5" />Exporter</Button>
            )}
            {sessionMeta.canCreate && (
              <Button asChild className="h-9" data-testid="trainings-new-session"><Link to="/formations/sessions/nouvelle"><Plus size={16} className="mr-1.5" />Nouvelle session</Link></Button>
            )}
          </div>
        )}
        {state.canManage && tab === "programmes" && (
          <Button onClick={() => setCreating(true)} className="h-9 shrink-0" data-testid="trainings-new-program"><Plus size={16} className="mr-1.5" />Nouveau programme</Button>
        )}
        {state.canManage && tab === "modules" && (
          <Button asChild className="h-9 shrink-0" data-testid="trainings-new-module"><Link to="/formations/modules/nouveau"><Plus size={16} className="mr-1.5" />Nouveau module</Link></Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 self-start" role="tablist" aria-label="Formations">
          {TABS.map(([key, label]) => (
            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => switchTab(key)} data-testid={`trainings-tab-${key}`}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${tab === key ? "bg-jamora text-white" : "text-slate-600 hover:text-slate-900"}`}>{label}</button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-72">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "modules" ? "Rechercher un module" : tab === "sessions" ? "Rechercher une session" : "Rechercher un programme"}
            className="pl-8 text-base md:text-sm" aria-label="Rechercher" data-testid="trainings-search" />
        </div>
      </div>

      {tab === "sessions" && (
        <SessionsPanel search={search} mine={mineOnly} onMeta={setSessionMeta} exportOpen={exportOpen} onExportOpenChange={setExportOpen} />
      )}
      {tab !== "sessions" && state.phase === "loading" && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {tab !== "sessions" && state.phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{state.error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {state.phase === "ready" && tab === "programmes" && (
        <ProgramList items={programs} total={state.programs.length} roles={catalog.roles} canManage={state.canManage} onCreate={() => setCreating(true)} />
      )}
      {state.phase === "ready" && tab === "modules" && <ModuleList items={modules} total={state.modules.length} canManage={state.canManage} />}

      <CreateProgramDialog open={creating} roles={catalog.roles} onClose={() => setCreating(false)}
        onCreated={(program) => navigate(`/formations/programmes/${program.id}`)} />
    </div>
  );
}

function EmptyCard({ icon: Icon, title, text, action, testid }) {
  return (
    <Card className="p-8 text-center space-y-2" data-testid={testid}>
      <Icon className="mx-auto text-slate-300" size={26} />
      <h3 className="font-medium text-slate-900">{title}</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">{text}</p>
      {action}
    </Card>
  );
}

function ProgramList({ items, total, roles, canManage, onCreate }) {
  if (!total) {
    return (
      <EmptyCard icon={GraduationCap} testid="trainings-programs-empty" title="Aucun programme pour le moment"
        text={canManage ? "Un programme enchaîne des modules, par exemple « Comptoir : vente et encaissement » pour les vendeurs."
          : "Les programmes publiés par l'équipe formation apparaîtront ici."}
        action={canManage && <Button variant="outline" onClick={onCreate} className="mt-2"><Plus size={14} className="mr-1.5" />Nouveau programme</Button>} />
    );
  }
  if (!items.length) return <Card className="p-6 text-center text-sm text-slate-500">Aucun programme ne correspond à la recherche.</Card>;
  return (
    <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="trainings-programs">
      {items.map((p) => (
        <Link key={p.id} to={`/formations/programmes/${p.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`training-program-row-${p.code}`}>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900 break-words">{p.label}</span>
              {canManage && <StatePill active={p.active} activeLabel="Actif" inactiveLabel="Désactivé" />}
            </div>
            <div className="text-xs text-slate-500">
              {audienceLabel(p, roles)}
              {" · "}{p.current_version ? `v${p.current_version.number} · ${programSummary(p.current_version)}` : "Jamais publié"}
              {p.draft_version ? ` · brouillon v${p.draft_version.number} en cours` : ""}
            </div>
          </div>
          <ChevronRight size={16} className="text-slate-300 shrink-0" />
        </Link>
      ))}
    </Card>
  );
}

function ModuleList({ items, total, canManage }) {
  if (!total) {
    return (
      <EmptyCard icon={BookOpen} testid="trainings-modules-empty" title="Aucun module pour le moment"
        text="Un module décrit ce que le participant saura faire (ses objectifs), les fonctionnalités Jamora concernées et sa durée."
        action={canManage && <Button variant="outline" asChild className="mt-2"><Link to="/formations/modules/nouveau"><Plus size={14} className="mr-1.5" />Nouveau module</Link></Button>} />
    );
  }
  if (!items.length) return <Card className="p-6 text-center text-sm text-slate-500">Aucun module ne correspond à la recherche.</Card>;
  return (
    <Card className="divide-y divide-slate-100 overflow-hidden" data-testid="trainings-modules">
      {items.map((m) => (
        <Link key={m.id} to={`/formations/modules/${m.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`training-module-row-${m.id}`}>
          <div className="min-w-0 flex-1 space-y-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-slate-900 break-words">{m.title}</span>
              {!m.active && <StatePill active={false} inactiveLabel="Désactivé" />}
            </div>
            <div className="text-xs text-slate-500">
              {[`${m.objectives.length} objectif${m.objectives.length > 1 ? "s" : ""}`, formatMinutes(m.duration_minutes),
                m.features.length ? `${m.features.length} fonctionnalité${m.features.length > 1 ? "s" : ""} Jamora` : null].filter(Boolean).join(" · ")}
            </div>
          </div>
          <ChevronRight size={16} className="text-slate-300 shrink-0" />
        </Link>
      ))}
    </Card>
  );
}

function CreateProgramDialog({ open, roles, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [audience, setAudience] = useState({ kind: "pharmacy", audience: [] });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setLabel(""); setDescription(""); setAudience({ kind: "pharmacy", audience: [] }); } }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/training/programs", {
        label: label.trim(), description: description.trim() || null, audience_kind: audience.kind, audience: audience.audience,
      });
      toast.success("Programme créé : ajoutez ses modules");
      onCreated(data);
    } catch (e) {
      toast.error(trainingError(e, "Impossible de créer le programme."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="program-create-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau programme</DialogTitle>
          <DialogDescription>Vous y ajouterez ensuite les modules, puis le publierez quand il sera prêt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="program-label">Nom *</Label>
            <Input id="program-label" maxLength={160} className="text-base md:text-sm" placeholder="Ex. Comptoir : vente et encaissement" value={label}
              onChange={(e) => setLabel(e.target.value)} data-testid="program-create-label" /></div>
          <div className="space-y-1.5"><Label htmlFor="program-description">Description</Label>
            <Textarea id="program-description" rows={2} maxLength={4000} className="text-base md:text-sm" value={description}
              onChange={(e) => setDescription(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Public visé</Label>
            <AudienceFields kind={audience.kind} audience={audience.audience} roles={roles} onChange={setAudience} idPrefix="program-create-audience" /></div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuler</Button>
          <Button onClick={create} disabled={busy || label.trim().length < 2} data-testid="program-create-confirm">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
